import React, { useCallback, useContext, useMemo } from 'react';
import EDIconButton from '../../../../../../../../../BaseComponents/Buttons/IconButton/EDIconButton';
import EDBox from '../../../../../../../../../BaseComponents/EDBox/EDBox';
import EDIcon from '../../../../../../../../../BaseComponents/Icon/EDIcon';
import AvenyTheme from '../../../../../../../../../BaseComponents/ThemeProvider/AvenyTheme';
import EDTooltip from '../../../../../../../../../BaseComponents/Tooltip/EDTooltip';
import EditableAttribute from '../../../../../../../../../Components/Attributes/EditableAttribute/EditableAttribute';
import { getAttribute } from '../../../../../../../../../Model/avenyModelUtils';
import { ModelContext } from '../../../../../../../../../Model/ModelContext/ModelContext';
import { Verifikationdetails_node_TEDEKBokfver__Associations_Ursprungsverifikation__Associations_Handelser__Associations_Levfaktura__Associations_Attestmeddelande_All } from '../../../../../../../Ekonomi/Rutiner/EkonomiskAnalys/Components/VerifikationModal/Queries/VerifikationQuery/_QueryTypes/Verifikationdetails';
import { BusinessDataFormContext } from '../../../../../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import AttestMessageContext from '../../../../../FakturaDel/Components/AttestMessageContext/AttestMessageContext';
import FakturaDelContext from '../../../../../FakturaDel/Components/FakturaDelContextManager/FakturaDelContext';
import { FakturaDelQuery_node_TEDLRFakturaDel } from '../../../../../FakturaDel/Queries/_QueryTypes/FakturaDelQuery';
import {
  FakturaDelType__Associations_Faktura__Associations_Attestmeddelande_All,
  FakturaDelType__Associations_Faktura__Associations_Attestmeddelande_All__Associations_Kvittenser_All,
  FakturaDelType__Associations_Faktura__Associations_Fakturadel,
} from '../../../../../FakturaDel/Queries/_QueryTypes/FakturaDelType';
import { getAttestantWithReserv } from '../../../../Utils/AttestMeddelandeUtils';

interface IProps {
  deleteOrUndoMessage: (
    event: React.MouseEvent<HTMLElement, MouseEvent>
  ) => void;
  deleteOrUndoMessageTooltip: string;
  hasChanged: boolean;
  hasMessageProp: boolean;
  index: number;
  isEditMode: boolean;
  message:
    | FakturaDelType__Associations_Faktura__Associations_Attestmeddelande_All
    | Verifikationdetails_node_TEDEKBokfver__Associations_Ursprungsverifikation__Associations_Handelser__Associations_Levfaktura__Associations_Attestmeddelande_All;
  setOriginalValue: React.Dispatch<React.SetStateAction<string>>;
  showEditButtons: boolean;
  showKvittensButton: boolean;
  sysOptionActive: boolean;
  kvittens:
    | FakturaDelType__Associations_Faktura__Associations_Attestmeddelande_All__Associations_Kvittenser_All
    | undefined;
  hasKvitterat: boolean;
}

const FakturaAttestMessage: React.FC<
  React.PropsWithChildren<IProps>
> = props => {
  const fakturaDelContext = useContext(FakturaDelContext);
  const formContext = useContext(BusinessDataFormContext);
  const modelContext = useContext(ModelContext);
  const attestMessageContext = useContext(AttestMessageContext);

  const attestMessageModel =
    modelContext.avenyCollectionMap.get('lrattestmeddelande');

  const attestMessageAttribute = getAttribute(
    'LRAttestmeddelande',
    ' ',
    'Attestmeddelande',
    modelContext.avenyCollectionMap
  );

  if (!attestMessageAttribute || !attestMessageModel) {
    throw new Error('Kunde inte hitta Attestmeddelande attribut eller model');
  }

  const getMessageLabel = useCallback(
    (
      attestMeddelande:
        | FakturaDelType__Associations_Faktura__Associations_Attestmeddelande_All
        | Verifikationdetails_node_TEDEKBokfver__Associations_Ursprungsverifikation__Associations_Handelser__Associations_Levfaktura__Associations_Attestmeddelande_All
    ): string => {
      // Check if AttestMeddelandeView is used by some other component than fakturaDel
      if (props.hasMessageProp) {
        return attestMeddelande.Rubrik ?? '';
      }

      if (!attestMeddelande.Rubrik) {
        const receiverID =
          attestMeddelande._Associations?.MottagarAttestratt?.id;

        const receiver = fakturaDelContext.allAttestanterOnAllParts.find(
          certifier => certifier?._Associations?.Attestratt?.id === receiverID
        );

        return receiver
          ? receiver.Attestant
            ? 'Nytt meddelande till ' + receiver.Attestant
            : 'Nytt meddelande till okänd'
          : 'Nytt meddelande';
      }

      const data: FakturaDelQuery_node_TEDLRFakturaDel = formContext?.data;

      const fakturaDelar:
        | FakturaDelType__Associations_Faktura__Associations_Fakturadel[]
        | null
        | undefined = data._Associations?.Faktura?._Associations?.Fakturadel;

      if (fakturaDelar) {
        const attestantName = getAttestantWithReserv(
          fakturaDelar,
          attestMeddelande as FakturaDelType__Associations_Faktura__Associations_Attestmeddelande_All
        );
        return attestantName
          ? `${attestMeddelande.Rubrik} (reserv för ${attestantName})`
          : attestMeddelande.Rubrik;
      }

      return attestMeddelande.Rubrik;
    },
    [
      fakturaDelContext.allAttestanterOnAllParts,
      formContext?.data,
      props.hasMessageProp,
    ]
  );

  const kvittensDate = useMemo(
    () => new Date(props.kvittens?.DATUM),
    [props.kvittens?.DATUM]
  );

  const showWarningColors =
    !props.hasMessageProp &&
    props.sysOptionActive &&
    props.showKvittensButton &&
    !props.hasKvitterat;

  return (
    <EDBox
      sx={{
        alignItems: 'center',
        borderBottom: `1px solid ${AvenyTheme.palette.divider} `,
        borderLeft: theme =>
          showWarningColors
            ? `4px solid ${theme.customProperties.Warning.main} `
            : 'none',
        display: 'flex',
        flexFlow: 'row nowrap',
        margin: `auto`,
        padding: `${AvenyTheme.spacing(0.5)} 0`,
        paddingRight: AvenyTheme.spacing(1.25),
        width: `calc(100% - ${AvenyTheme.spacing(1.25)})`,
        wordBreak: 'break-word',
        height: 'auto',
        '&:last-of-type': {
          borderBottom: '0',
        },
        backgroundColor: theme =>
          showWarningColors
            ? theme.customProperties.Warning.ultraUltraLight
            : 'inherit',
      }}
      testID={`message-${props.index}`}
    >
      <EditableAttribute
        onFocus={(event: React.FocusEvent<HTMLElement>) => {
          const messageLength = props.message.MEDDELANDE?.length ?? 0;
          (event.target as HTMLInputElement).setSelectionRange(
            messageLength,
            messageLength
          );
        }}
        autoFocus
        attribute={attestMessageAttribute}
        collection={attestMessageModel}
        disabled={!props.isEditMode}
        disablePaddingRight
        editMode={props.isEditMode}
        marker={props.isEditMode && props.hasChanged}
        endAdornment={
          <EDIconButton
            icon="delete"
            iconSize="medium"
            margin="none"
            onClick={props.deleteOrUndoMessage}
            padding="dense"
            tooltip={props.deleteOrUndoMessageTooltip}
            testId="deleteMessageButtonAdornment"
          />
        }
        fullWidth
        label={getMessageLabel(props.message)}
        multiline
        onChange={value => {
          fakturaDelContext.addAttestMessage(
            value,
            props.message.id,
            undefined,
            attestMessageContext.messageReciverID,
            props.message.MOTTAGAR_TYP
          );
        }}
        size="large"
        value={props.message.MEDDELANDE ?? ''}
        valueNewLine
      />
      {props.showEditButtons && (
        <>
          <EDIconButton
            icon="edit"
            iconSize="medium"
            margin="none"
            onClick={(event: React.MouseEvent<HTMLElement>) => {
              event.stopPropagation();
              props.setOriginalValue(props.message.MEDDELANDE ?? '');
              attestMessageContext.setMessageEditModeID(props.message.id);
            }}
            padding="dense"
            tooltip="Redigera meddelandet"
            testId="editMessageButton"
          />

          <EDIconButton
            icon="delete"
            iconSize="medium"
            margin="none"
            onClick={() => {
              fakturaDelContext.deleteOrUndoAttestMessage(props.message.id);
            }}
            padding="dense"
            tooltip="Ta bort meddelandet"
            testId="deleteMessageButton"
          />
        </>
      )}
      {!props.hasMessageProp && props.sysOptionActive && (
        <>
          {props.showKvittensButton ? (
            <EDIconButton
              icon="email"
              iconSize="medium"
              margin="none"
              onClick={() => {
                fakturaDelContext.readMessage(props.message.id);
              }}
              padding="dense"
              tooltip="Kvittera meddelandet"
              iconColor="warning"
              testId="signOffMessageButton"
            />
          ) : undefined}
          {props.kvittens && props.hasKvitterat && (
            <EDTooltip
              title={`Kvitterat ${kvittensDate.toLocaleDateString(
                'sv-SE'
              )}, ${kvittensDate.toLocaleTimeString('sv-SE', {
                timeStyle: 'short',
              })}`}
              enterDelay={500}
              enterTouchDelay={500}
            >
              <EDBox component="span">
                <EDIcon iconType="drafts" color="secondary" />
              </EDBox>
            </EDTooltip>
          )}
        </>
      )}
    </EDBox>
  );
};

export default FakturaAttestMessage;
